import React, { useEffect, useState } from "react";
import { useNavigate, NavLink, Outlet } from "react-router-dom";
import { manchiApi, manchiAuth } from "@/lib/manchiApi";
import { Button } from "@/components/ui/button";
import { LogOut, Heart, PenLine, Globe2, Newspaper, FileText, Vault, Sun } from "lucide-react";
import { toast } from "sonner";

export default function ManchiShell() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!manchiAuth.getToken()) { nav("/manchi", { replace: true }); return; }
    manchiApi.get("/me").then(({ data }) => setMe(data)).catch((err) => {
      if (err?.response?.status === 401) {
        manchiAuth.clear();
        toast.error("Session expired");
        nav("/manchi", { replace: true });
      }
    });
  }, [nav]);

  const logout = () => {
    manchiAuth.clear();
    toast.success("Signed out");
    nav("/manchi", { replace: true });
  };

  if (!me) return null;

  const tabs = [
    { to: "/manchi/studio", label: "Compose", icon: PenLine, end: true, testid: "manchi-nav-compose" },
    { to: "/manchi/studio/daily", label: "Daily", icon: Sun, testid: "manchi-nav-daily" },
    { to: "/manchi/studio/globe", label: "Globe", icon: Globe2, testid: "manchi-nav-globe" },
    { to: "/manchi/studio/sources", label: "Sources", icon: Newspaper, testid: "manchi-nav-sources" },
    { to: "/manchi/studio/drafts", label: "Drafts", icon: FileText, testid: "manchi-nav-drafts" },
    { to: "/manchi/studio/vault", label: "Vault", icon: Vault, testid: "manchi-nav-vault" },
  ];

  return (
    <div className="min-h-screen text-rose-950" style={{ background: "linear-gradient(180deg,#fff5f9 0%,#fdf2f8 100%)" }}>
      <header className="sticky top-0 z-20 border-b border-pink-100 bg-white/70 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center shadow-md shadow-pink-300/50">
              <Heart className="w-5 h-5 text-white" fill="white" />
            </div>
            <div>
              <div className="font-black text-rose-900 leading-none text-lg" data-testid="manchi-agent-name">{me.agent_name}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-rose-400 mt-1">econ &amp; finance studio</div>
            </div>
          </div>

          <nav className="flex items-center gap-1 overflow-x-auto">
            {tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={t.testid}
                className={({ isActive }) =>
                  `text-[12px] font-semibold px-3 py-2 rounded-xl flex items-center gap-1.5 whitespace-nowrap transition ${
                    isActive
                      ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow shadow-pink-300/40"
                      : "text-rose-500 hover:bg-pink-100"
                  }`
                }
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </NavLink>
            ))}
          </nav>

          <Button
            onClick={logout}
            data-testid="manchi-logout-btn"
            variant="outline"
            className="rounded-xl border-pink-200 text-rose-500 hover:bg-pink-100 hover:text-rose-600"
          >
            <LogOut className="w-4 h-4 mr-1.5" /> Exit
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        <Outlet context={{ me }} />
      </main>
    </div>
  );
}
